import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFetchOrderQuery } from "../slices/checkoutSlice";
import Button from "../components/utilComponents/Button";
import Loader from "../components/utilComponents/Loader";
import Error from "../components/message/Error";

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const { data: order, isLoading, error } = useFetchOrderQuery(orderId);
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div>
        <div className="md:hidden mx-2">
          <Loader count={1} columns={1} itemClass="h-[320px]" />
          <Loader count={1} columns={1} itemClass="h-[200px]" />
        </div>
        <div className="hidden md:block container">
          <Loader count={2} columns={2} itemClass="h-[320px]" />
        </div>
      </div>
    );
  }

  if (error || !order)
    return <Error message="Unable to fetch order details." />;

  const isPaid = order.paymentStatus === "paid";
  const isUnpaid = order.paymentStatus === "unpaid";
  const isFailed = order.paymentStatus === "failed";

  return (
    <div>
      {/* Desktop View */}
      <div className="hidden lg:flex m-2 md:container flex flex-col space-y-4 md:flex-row md:space-x-8 md:space-y-0 mx-2">
        {/* Order Details Section */}
        <div className="flex-4 order-summary">
          {isPaid && (
            <h3 className="font-nunito text-agBlack font-semibold text-lg mb-4 text-green-600">
              ✅ Thank you! Your payment was successful.
            </h3>
          )}
          {isUnpaid && (
            <h3 className="font-nunito text-agBlack font-semibold text-lg mb-4 text-yellow-600">
              ⚠️ Your order is pending payment.
            </h3>
          )}
          {isFailed && (
            <h3 className="font-nunito text-agBlack font-semibold text-lg mb-4 text-red-600">
              ❌ Payment Failed. Please try again.
            </h3>
          )}

          <div className="mb-4">
            <p className="text-sm text-agLightGray">Order #{orderId}</p>
            <p className="text-sm text-agLightGray">
              {order.shippingInfo.firstName} {order.shippingInfo.lastName}
            </p>
            <p className="text-sm text-agLightGray">
              {order.shippingInfo.address}
            </p>
            <p className="text-sm text-agLightGray">
              {order.shippingInfo.phoneNumber}
            </p>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-lg mb-4">Order Items</h4>
            {order.orderItems.map((item) => (
              <div
                key={item.productId}
                className="flex items-center space-x-4 border-b pb-4"
              >
                <img
                  src={item.productImage || "/placeholder.jpg"}
                  alt={item.productTitle}
                  className="w-20 h-20 object-cover rounded-md"
                />
                <div className="flex-1">
                  <p className="font-nunito text-agBlack text-sm">
                    {item.productTitle || "Untitled Product"}
                  </p>
                  <p className="text-xs text-agLightGray">
                    Qty: {item.qty} × ₦{item.price.toLocaleString("en-NG")}
                  </p>
                </div>
                <p className="text-sm font-semibold text-agBlack">
                  ₦{(item.qty * item.price).toLocaleString("en-NG")}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary Section */}
        <div className="flex-2 w-full lg:w-[30vw]">
          <div className="order-summary">
            <h3 className="font-nunito text-agBlack font-semibold text-lg">
              Order Summary
            </h3>
            <hr className="mb-4" />
            <div className="order-summary-details">
              <div className="order-summary-item">
                <span className="text-sm text-agLightGray">Date</span>
                <span className="text-sm text-agBlack">
                  {new Date(order.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div className="order-summary-item">
                <span className="text-sm text-agLightGray">Subtotal</span>
                <span className="text-sm text-agBlack">
                  ₦{order.subTotal.toLocaleString("en-NG")}
                </span>
              </div>
              <div className="order-summary-item">
                <span className="text-sm text-agLightGray">Shipping</span>
                <span className="text-sm text-agBlack">
                  ₦{order.shipping.toLocaleString("en-NG")}
                </span>
              </div>
              <div className="order-summary-item">
                <span className="text-sm text-agLightGray">Tax</span>
                <span className="text-sm text-agBlack">
                  ₦{order.tax.toLocaleString("en-NG")}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total</span>
                <span className="text-agBlack font-nunito font-bold text-lg">
                  ₦{order.total.toLocaleString("en-NG")}
                </span>
              </div>
            </div>

            {isPaid ? (
              <Button variant="primary" fullWidth onClick={() => navigate("/")}>
                CONTINUE SHOPPING
              </Button>
            ) : (
              <Button
                variant="primary"
                fullWidth
                onClick={() => navigate(`/checkout/review/${orderId}`)}
              >
                COMPLETE PAYMENT
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="lg:hidden m-2 flex flex-col space-y-4">
        <div>
          {isPaid && (
            <h3 className="font-nunito text-agBlack font-semibold text-lg mb-4 text-green-600">
              ✅ Thank you! Your payment was successful.
            </h3>
          )}
          {isUnpaid && (
            <h3 className="font-nunito text-agBlack font-semibold text-lg mb-4 text-yellow-600">
              ⚠️ Your order is pending payment.
            </h3>
          )}
          {isFailed && (
            <h3 className="font-nunito text-agBlack font-semibold text-lg mb-4 text-red-600">
              ❌ Payment Failed. Please try again.
            </h3>
          )}
          <p className="text-sm text-agLightGray">Order #{orderId}</p>
          <p className="text-sm text-agLightGray">
            {order.shippingInfo.firstName} {order.shippingInfo.lastName}
          </p>
          <p className="text-sm text-agLightGray">
            {order.shippingInfo.address}
          </p>
          <p className="text-sm text-agLightGray">
            {order.shippingInfo.phoneNumber}
          </p>

          <div className="mt-6 space-y-4">
            <h4 className="font-semibold text-lg mb-2">Order Items</h4>
            {order.orderItems.map((item) => (
              <div
                key={item.productId}
                className="flex items-center space-x-4 border-b pb-4"
              >
                <img
                  src={item.productImage || "/placeholder.jpg"}
                  alt={item.productTitle}
                  className="w-20 h-20 object-cover rounded-md"
                />
                <div className="flex-1">
                  <p className="font-nunito text-agBlack text-sm">
                    {item.productTitle || "Untitled Product"}
                  </p>
                  <p className="text-xs text-agLightGray">
                    Qty: {item.qty} × ₦{item.price.toLocaleString("en-NG")}
                  </p>
                </div>
                <p className="text-sm font-semibold text-agBlack">
                  ₦{(item.qty * item.price).toLocaleString("en-NG")}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="order-summary">
          <h3 className="font-nunito text-agBlack font-semibold text-lg">
            Order Summary
          </h3>
          <hr className="mb-4" />
          <div className="order-summary-details">
            <div className="order-summary-item">
              <span className="text-sm text-agLightGray">Date</span>
              <span className="text-sm text-agBlack">
                {new Date(order.createdAt).toLocaleDateString()}
              </span>
            </div>
            <div className="order-summary-item">
              <span className="text-sm text-agLightGray">Subtotal</span>
              <span className="text-sm text-agBlack">
                ₦{order.subTotal.toLocaleString("en-NG")}
              </span>
            </div>
            <div className="order-summary-item">
              <span className="text-sm text-agLightGray">Shipping</span>
              <span className="text-sm text-agBlack">
                ₦{order.shipping.toLocaleString("en-NG")}
              </span>
            </div>
            <div className="order-summary-item">
              <span className="text-sm text-agLightGray">Tax</span>
              <span className="text-sm text-agBlack">
                ₦{order.tax.toLocaleString("en-NG")}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Total</span>
              <span className="text-agBlack font-nunito font-bold text-lg">
                ₦{order.total.toLocaleString("en-NG")}
              </span>
            </div>
          </div>

          {isPaid ? (
            <Button variant="primary" fullWidth onClick={() => navigate("/")}>
              CONTINUE SHOPPING
            </Button>
          ) : (
            <Button
              variant="primary"
              fullWidth
              onClick={() => navigate(`/checkout/review/${orderId}`)}
            >
              COMPLETE PAYMENT
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;

// import React from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { useFetchOrderQuery } from "../slices/checkoutSlice";
// import Button from "../components/utilComponents/Button";

// const OrderConfirmation = () => {
//   const { orderId } = useParams();
//   const { data: order, isLoading, error } = useFetchOrderQuery(orderId);
//   const navigate = useNavigate();

//   if (isLoading) return <div>Loading...</div>;
//   if (error) return <div>Error: Unable to fetch order details.</div>;

//   return (
//     <div className="checkout-container">
//       <h3>Thank you for your Order!</h3>
//       <div>
//         <p>Order #{orderId}</p>
//         <p>
//           {order.shippingInfo.firstName} {order.shippingInfo.lastName}
//         </p>
//         <p>{order.shippingInfo.address}</p>
//         <p>{order.shippingInfo.phoneNumber}</p>
//         {/* <p>Delivery between 8:00am - 6:00pm, 23 April</p> */}
//       </div>

//       <div className="order-summary">
//         <h4>Order Summary</h4>
//         <p>Date: {new Date(order.createdAt).toLocaleDateString()}</p>
//         <p>Order #: {orderId}</p>
//         <p>Subtotal: ₦{order.subTotal.toLocaleString()}</p>
//         <p>Shipping: ₦{order.shipping.toLocaleString()}</p>
//         <p>Tax: ₦{order.tax.toLocaleString()}</p>
//         <p>Total: ₦{order.total.toLocaleString()}</p>
//       </div>

//       <Button variant="primary" onClick={() => navigate("/")} fullWidth>
//         CONTINUE SHOPPING
//       </Button>
//     </div>
//   );
// };

// export default OrderConfirmation;
